import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { usePropertyField, useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { resolveScheduleTourHref } from '../utils/scheduleTourHref';

const primaryCtaDef: SlotGroupDefinition = {
  label: 'Primary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const secondaryCtaDef: SlotGroupDefinition = {
  label: 'Secondary CTA',
  fields: {
    // No defaultValue — href is computed at render from the AnyoneHome
    // scheduler URL (→ /schedule-a-tour) or Contact Us fallback.
    url: { label: 'URL', type: 'url' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function ThankYouContent() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const primaryCta = useSlotGroup('thankyoucontent.primary_cta', primaryCtaDef);
  const secondaryCta = useSlotGroup('thankyoucontent.secondary_cta', secondaryCtaDef);
  const anyoneHomeSchedulerUrl = usePropertyField('custom_anyonehome_scheduler_url') as string | null;
  const secondaryCtaHref = resolveScheduleTourHref(secondaryCta.url, anyoneHomeSchedulerUrl);

  return (
    <section ref={sectionRef as React.RefObject<HTMLElement>} className="reveal-fade-up py-16 md:py-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <RichTextContent
          path="thankyoucontent.body"
          label="Body Text"
          defaultValue="<p>Thank you for your interest! A team member will reach out to you soon to help you find your dream home.</p>"
          as="div"
          className="font-body text-base md:text-lg leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4"
        />

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
          <EditableSlotGroup groupPath="thankyoucontent.primary_cta" definition={primaryCtaDef}>
            <a
              href={primaryCta.url || '/floor-plans'}
              target={primaryCta._target || '_self'}
              className="thankyou-cta"
            >
              {primaryCta.label || 'View Floor Plans'}
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="thankyoucontent.secondary_cta" definition={secondaryCtaDef}>
            <a
              href={secondaryCtaHref}
              target={secondaryCta._target || '_self'}
              className="thankyou-cta"
            >
              {secondaryCta.label || 'Schedule a Tour'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .thankyou-cta {
          display: inline-block;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--vibe-color-text-on-primary);
          background: var(--vibe-color-primary);
          padding: 0.75rem 2rem;
          border-radius: 9999px;
          text-decoration: none;
          transition: opacity 0.2s;
        }
        .thankyou-cta:hover {
          opacity: 0.85;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ThankYouContent',
  displayName: 'Thank You Content',
  tokens: {
    'thankyoucontent.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue: '<p>Thank you for your interest! A team member will reach out to you soon to help you find your dream home.</p>',
    },
  },
  slotGroups: {
    'thankyoucontent.primary_cta': {
      label: 'Primary CTA',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'thankyoucontent.secondary_cta': {
      label: 'Secondary CTA',
      fields: {
        // No defaultValue — href is computed at render from the AnyoneHome
        // scheduler URL (→ /schedule-a-tour) or Contact Us fallback.
        url: { label: 'URL', type: 'url' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
};

(ThankYouContent as any).contentSchema = contentSchema;

export default ThankYouContent;
